import { Tooltip } from '@civilgrid/ui/components';
import type { ColumnDef } from '@tanstack/react-table';
import { isEmpty } from 'lodash';
import type { ReactNode } from 'react';
import { proxySet } from 'valtio/utils';
import Checkbox from '~/components2/forms/Checkbox';
import InfoTooltip from '~/components2/InfoTooltip';
import { AUTO_COL_WIDTH } from '~/components2/Table';
import { isConnectbaseCovered, type UtilityAgency } from '../../../../common';
import EstTimeCell from '../../../../common/EstTimeCell';
import { makeEstTimeSortHeader } from '../../../../common/EstTimeSortHeader';
import {
  type EstTimeRequest,
  type EstTimeState,
  getEstTimeState,
  getResponseConfidenceRank,
  surfacedEstimateDays,
} from '../../../../common/estimatedResponseTime';
import store from '../../../../store';
import ConstraintPill from './ConstraintPill';

const CHECK = '✅';

/** Whether the drawer offers a maps request for this agency. A Connectbase-covered carrier reads like published data. */
export const isMapsRequestable = (a: UtilityAgency): boolean =>
  !!a.dataRequestable && !isConnectbaseCovered(a, store.connectbaseCoverageEnabled);

/**
 * Selected agency ids to submit as maps requests. Re-checks requestability so a selection made before the
 * coverage flag resolved is not submitted for a carrier that now has no maps checkbox.
 */
export const pendingMapsRequestIds = (
  selection: Iterable<string>,
  agencies: Record<string, UtilityAgency>,
  alreadyRequested: ReadonlySet<string>,
): string[] =>
  [...selection].filter(
    (id) => !alreadyRequested.has(id) && agencies[id] !== undefined && isMapsRequestable(agencies[id]),
  );

export type ConstraintFieldNames = keyof Pick<UtilityAgency, 'chargesMapFees' | 'requiresNda' | 'requiresLetterhead'>;

// Custom sortable header: clicking cycles est time → confidence → off (via the hidden estTimeDays /
// estTimeConfidence columns). Shared with the main utilities table; only the test ids and PostHog
// `source` differ.
const renderEstTimeHeader = makeEstTimeSortHeader({ testIdPrefix: 'select-assets', source: 'select_assets' });

// The agency's existing requests, keyed the way the drawer keys agencies (the project-agency row id).
const agencyRequests = (agencyId: string): EstTimeRequest[] =>
  Object.values(store.dataRequests).filter((r) => r.agency.id === agencyId);

// A row with a request in flight shows the date it was placed, and keeps its range on hover.
const estTimeState = (row: UtilityAgency) => getEstTimeState(row, agencyRequests(row.id));

// Kinds whose cell carries text rather than a bare dash. `requested`, `delivered` and `completed`
// qualify on the request alone, so they count even on the project-agencies-job-off payload, which
// omits responseStats.
const CELL_HAS_CONTENT: ReadonlySet<EstTimeState['kind']> = new Set([
  'estimate',
  'requested',
  'delivered',
  'completed',
]);

/**
 * Whether any agency has something other than a bare dash to show. Drives hiding the whole column when
 * every row would render a dash (killswitch on, nothing in flight, and no usable stats).
 */
export const anyAgencyHasEstTime = (agencies: readonly UtilityAgency[]): boolean =>
  agencies.some((a) => CELL_HAS_CONTENT.has(estTimeState(a).kind));

export const columns: ColumnDef<UtilityAgency>[] = [
  {
    header: 'Agency',
    size: AUTO_COL_WIDTH,
    accessorKey: 'agencyName',
  },
  {
    header: 'Asset',
    accessorKey: 'type',
    size: 75,
  },
  {
    id: 'maps',
    size: 50,
    header: ({ table }) => {
      const validAgencies = table
        .getRowModel()
        .rows.filter((r) => isMapsRequestable(r.original))
        .map((r) => r.id)
        .filter((id) => !store.mapRequestAgencyIds.has(id));

      const isDisabled = validAgencies.length === 0;

      const allChecked = validAgencies.every((id) => store.formState.mapsSelection.has(id)) || isDisabled;

      return (
        <div className="flex h-full w-full flex-col items-center justify-end">
          <div>Maps</div>
          <Checkbox
            data-testid="request-data-maps-select-all"
            label=""
            checked={allChecked}
            value={allChecked}
            disabled={isDisabled}
            onChange={(checked) => {
              if (checked) {
                store.formState.mapsSelection = proxySet(validAgencies);
              } else {
                store.formState.mapsSelection.clear();
              }

              table.options.meta?.updateData?.();
            }}
          />
        </div>
      );
    },
    cell: (cell) => {
      if (!isMapsRequestable(cell.row.original)) return null;
      const id = cell.row.original.id;

      const isRequested = store.mapRequestAgencyIds.has(id);
      if (isRequested) {
        return (
          <Tooltip
            text="Request In Progress"
            aria-label="Request in progress"
            triggerClassName="flex w-full cursor-help justify-center"
            data-testid={`request-data-maps-requested-${id}`}
          >
            {CHECK}
          </Tooltip>
        );
      }
      const isChecked = store.formState.mapsSelection.has(id) || isRequested;

      return (
        <div className="flex justify-center">
          <Checkbox
            data-testid={`request-data-maps-${id}`}
            label=""
            checked={isChecked}
            value={isChecked}
            disabled={isRequested}
            onChange={(checked) => {
              if (checked) {
                store.formState.mapsSelection.add(id);
              } else {
                store.formState.mapsSelection.delete(id);
              }
              cell.table.options.meta?.updateData?.();
            }}
          />
        </div>
      );
    },
  },
  {
    id: 'asbuilts',
    size: 75,
    header: ({ table }) => {
      const validAgencies = table
        .getRowModel()
        .rows.filter((r) => r.original.asbuiltsAvailable)
        .map((r) => r.id)
        .filter((id) => !store.asbuiltRequestAgencyIds.has(id));

      const isDisabled = validAgencies.length === 0;

      const allChecked = validAgencies.every((id) => store.formState.asbuiltsSelection.has(id)) || isDisabled;

      return (
        <div className="flex w-full flex-col items-center justify-center">
          <div>As-Builts</div>

          <Checkbox
            data-testid="request-data-asbuilts-select-all"
            label=""
            checked={allChecked}
            value={allChecked}
            disabled={isDisabled}
            onChange={(checked) => {
              if (checked) {
                store.formState.asbuiltsSelection = proxySet(validAgencies);
              } else {
                store.formState.asbuiltsSelection.clear();
              }

              table.options.meta?.updateData?.();
            }}
          />
        </div>
      );
    },
    cell: (cell) => {
      if (!cell.row.original.asbuiltsAvailable) return null;
      const id = cell.row.original.id;
      const isRequested = store.asbuiltRequestAgencyIds.has(id);

      if (isRequested) {
        return (
          <Tooltip
            text="Request In Progress"
            aria-label="Request in progress"
            triggerClassName="flex w-full cursor-help justify-center"
            data-testid={`request-data-asbuilts-requested-${id}`}
          >
            {CHECK}
          </Tooltip>
        );
      }

      const isChecked = store.formState.asbuiltsSelection.has(id) || isRequested;

      return (
        <div className="flex justify-center">
          <Checkbox
            data-testid={`request-data-asbuilts-${id}`}
            label=""
            checked={isChecked}
            value={isChecked}
            disabled={isRequested}
            onChange={(checked) => {
              if (checked) {
                store.formState.asbuiltsSelection.add(id);
              } else {
                store.formState.asbuiltsSelection.delete(id);
              }
              cell.table.options.meta?.updateData?.();
            }}
          />
        </div>
      );
    },
  },
  {
    id: 'estTime',
    // Display-only: sorting is driven by the hidden estTimeDays / estTimeConfidence columns via the
    // custom header, so TableV2 does not add its own asc/desc arrow or click handler on this column.
    enableSorting: false,
    // Wide enough for the header (label + info tooltip + sort-icon slot) without clipping.
    size: 100,
    header: renderEstTimeHeader,
    cell: ({ row }) => <EstTimeCell state={estTimeState(row.original)} testId="select-assets-est-time-cell" />,
  },
  {
    // Hidden helper column backing the "sort by est time" mode. Raw days (numeric), not the bucket label,
    // so sorting orders numerically. Only rows that surface a range have days; killswitched, closed-out
    // and no-stats rows land undefined and sort last, matching their cells.
    id: 'estTimeDays',
    accessorFn: (row) => surfacedEstimateDays(estTimeState(row)) ?? undefined,
    sortUndefined: 'last',
    enableHiding: true,
    // Never rendered, so it must take zero layout width. TableV2's auto-resize subtracts every column's
    // size (hidden included) from the available width; a default-sized hidden column would steal ~150px
    // and can push the Agency column to its un-resized sentinel width in the narrow request drawer.
    size: 0,
    minSize: 0,
  },
  {
    // Hidden helper column backing the "sort by confidence" mode. Never rendered (visibility forced off);
    // ranks only rows that surface a range, others sort last.
    id: 'estTimeConfidence',
    accessorFn: (row) =>
      surfacedEstimateDays(estTimeState(row)) === null ? undefined : getResponseConfidenceRank(row),
    sortUndefined: 'last',
    enableHiding: true,
    // Zero-width for the same reason as estTimeDays above: hidden sort-only column, must not consume
    // layout width in TableV2's auto-resize.
    size: 0,
    minSize: 0,
  },
  {
    id: 'constraints',
    header: () => (
      <div className="flex items-center space-x-1">
        <div>Constraints</div>
        <InfoTooltip
          label="About constraints"
          text={
            <div>
              Things you should be prepared for prior to initiating a request from this agency:*
              <ul className="list-inside list-decimal"></ul>
              <li>
                Agency is known to charges map fees. CivilGrid will communicate the map fee estimate from the agency
                once received. CivilGrid cannot estimate map fees in advance.
              </li>
              <li>
                Agency requires agency letterhead in order to waive map fees. Several agencies will waive fees for
                projects done for public sector clients.
              </li>
              <li>Agency requires NDA.</li>
              <li>
                Agency has not historically responded to prior map requests. If you have additional information on how
                to contact this agency, please let us know in the (display icon) Project Channel.
              </li>
              This constraint list are not exhaustive. Agencies may ask for fees, NDAs, etc. not listed here.
            </div>
          }
        />
      </div>
    ),
    size: 110,
    cell: ({ row }) => {
      const agency = row.original;
      const els: ReactNode[] = [];

      if (agency.chargesMapFees) {
        els.push(
          <ConstraintPill
            key={'map-fee'}
            title="Agency Fee"
            color="green"
            content={
              <>
                <div>Agency charges a fee to share documents.</div>

                {!isEmpty(agency.mapFeeNote) ? (
                  <>
                    <br />
                    <div>{agency.mapFeeNote}</div>
                  </>
                ) : null}

                {agency.averageMapFeePerPage ? (
                  <>
                    <br />
                    <div>
                      Based on past requests, average map fee per page is ${agency.averageMapFeePerPage}. Requesting
                      exact cost from agency typically adds significant delays to requests.
                    </div>
                  </>
                ) : null}
              </>
            }
          />,
        );
      }
      if (agency.requiresLetterhead) {
        els.push(
          <ConstraintPill
            key={'letterhead'}
            title="Letterhead"
            color="blue"
            content={
              <div>
                Agency requires the request to be initiated on client letterhead. Samples will be provided in the next
                step.
              </div>
            }
          />,
        );
      }
      if (agency.requiresNda) {
        els.push(
          <ConstraintPill
            key={'nda'}
            title="NDA"
            color="red"
            content={
              <div>
                Agency requires an NDA to be completed before sharing maps. Samples will be provided in the next step.
              </div>
            }
          />,
        );
      }

      return <div className="flex flex-col space-y-1">{els}</div>;
    },
  },
];
